import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../context/AuthContext';
import {
  Plus, Trash2, Loader2, RefreshCw, CheckCircle2, XCircle, Pencil, History, Pin,
  Search, RotateCcw, Save, X, Brain, FlaskConical,
} from 'lucide-react';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Archived' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const EMPTY_FORM = { id: null, title: '', content: '', kind: 'fact', scope: 'global', scope_key: '', tags: '', pinned: false };

const errMsg = (e) => (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || String(e);
const fmtTime = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch (_) { return s; } };

function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-600',
    brand: 'bg-brand-50 text-brand-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-600',
    green: 'bg-emerald-50 text-emerald-700',
    violet: 'bg-violet-50 text-violet-700',
  };
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${tones[tone] || tones.slate}`}>{children}</span>;
}

const statusTone = { active: 'green', pending: 'amber', rejected: 'red', archived: 'slate' };

function MemoryForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState(initial || EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async (force) => {
    setBusy(true); setErr('');
    try {
      const body = { ...f, force: !!force };
      const { data } = f.id ? await api.put(`/api/ai/memory/${f.id}`, body) : await api.post('/api/ai/memory', body);
      if (data && data.ok === false) { setErr(data.message || 'Not saved.'); setBusy(false); return; }
      onSaved(data);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="border border-brand-200 bg-brand-50/40 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{f.id ? `Edit memory #${f.id}` : 'New memory'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
      </div>
      <input value={f.title} onChange={e => set('title', e.target.value)} placeholder="Title (one line)"
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white" />
      <textarea value={f.content} onChange={e => set('content', e.target.value)} rows={6}
        placeholder={f.kind === 'skill' ? 'Procedure — numbered steps that worked' : 'The fact: root cause, pitfall, rule… (Markdown allowed)'}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white font-mono" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="text-xs text-slate-500">Kind
          <select value={f.kind} onChange={e => set('kind', e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
            <option value="fact">Fact</option>
            <option value="skill">Skill (procedure)</option>
          </select>
        </label>
        <label className="text-xs text-slate-500">Scope
          <select value={f.scope} onChange={e => set('scope', e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
            <option value="global">Global (all chats)</option>
            <option value="source">Dashboard / datasource</option>
            <option value="user">User</option>
          </select>
        </label>
        <label className="text-xs text-slate-500">{f.scope === 'user' ? 'Username' : 'Datasource key'}
          <input value={f.scope === 'global' ? '' : f.scope_key} disabled={f.scope === 'global'}
            onChange={e => set('scope_key', e.target.value)} placeholder={f.scope === 'global' ? '—' : f.scope === 'user' ? 'e.g. admin' : 'e.g. sap_roles'}
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:bg-slate-50" />
        </label>
        <label className="text-xs text-slate-500">Tags
          <input value={f.tags} onChange={e => set('tags', e.target.value)} placeholder="p01,performance"
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg bg-white" />
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={!!f.pinned} onChange={e => set('pinned', e.target.checked)} />
        Pinned — always injected into every matching chat, regardless of similarity (use for landscape guardrails)
      </label>
      {err && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start justify-between gap-3">
          <span>{err}</span>
          {/similar memory already exists/i.test(err) && !f.id && (
            <button onClick={() => submit(true)} className="shrink-0 underline">Save anyway</button>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button onClick={() => submit(false)} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
    </div>
  );
}

function HistoryPanel({ item, onClose, onRestored }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/api/ai/memory/${item.id}/history`).then(r => setRows(r.data.items || [])).catch(e => setErr(errMsg(e)));
  }, [item.id]);
  const restore = async (h) => {
    if (!window.confirm(`Restore memory #${item.id} to the version from ${fmtTime(h.changed_at)}?`)) return;
    try { await api.post(`/api/ai/memory/${item.id}/history/${h.id}/restore`); onRestored(); }
    catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">Version history</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {!rows ? <Loader2 size={14} className="animate-spin text-slate-400" /> : rows.length === 0 ? (
        <div className="text-xs text-slate-400">No history.</div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-auto">
          {rows.map(h => (
            <div key={h.id} className="text-xs bg-slate-50 rounded-lg p-2">
              <div className="flex items-center justify-between gap-2 text-slate-500">
                <span><b className="text-slate-600">{h.action}</b> · {h.changed_by || '—'} · {fmtTime(h.changed_at)} · {h.status}</span>
                <button onClick={() => restore(h)} className="inline-flex items-center gap-1 text-brand-700 hover:underline"><RotateCcw size={12} /> Restore</button>
              </div>
              <div className="mt-1 font-medium text-slate-700">{h.title}</div>
              <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-slate-600">{h.content}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemoryCard({ item, onChanged, onEdit }) {
  const [busy, setBusy] = useState('');
  const [showHist, setShowHist] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [orig, setOrig] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (item.status === 'pending' && item.replaces_id) {
      api.get(`/api/ai/memory/${item.replaces_id}`).then(r => setOrig(r.data.item)).catch(() => {});
    }
  }, [item.status, item.replaces_id]);

  const act = async (name, fn) => {
    setBusy(name); setErr('');
    try { await fn(); onChanged(); } catch (e) { setErr(errMsg(e)); }
    setBusy('');
  };
  const long = item.content.length > 280;

  return (
    <div className={`bg-white border rounded-xl p-4 ${item.status === 'pending' ? 'border-amber-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className="text-xs text-slate-400">#{item.id}</span>
            <Badge tone={statusTone[item.status]}>{item.status}</Badge>
            <Badge tone={item.kind === 'skill' ? 'violet' : 'brand'}>{item.kind}</Badge>
            <Badge>{item.scope === 'global' ? 'global' : `${item.scope}: ${item.scope_key}`}</Badge>
            {item.pinned && <Badge tone="amber"><Pin size={10} /> pinned</Badge>}
            {!item.has_vector && <Badge tone="red">no embedding</Badge>}
          </div>
          <div className="text-sm font-semibold text-slate-800">{item.title}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {item.status === 'pending' && (<>
            <button title="Approve" disabled={!!busy} onClick={() => act('approve', () => api.post(`/api/ai/memory/${item.id}/approve`))}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">
              {busy === 'approve' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
            </button>
            <button title="Reject" disabled={!!busy} onClick={() => act('reject', () => api.post(`/api/ai/memory/${item.id}/reject`, {}))}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg">
              <XCircle size={12} /> Reject
            </button>
          </>)}
          {item.status !== 'archived' && item.status !== 'rejected' && (
            <button title="Edit" onClick={() => onEdit(item)} className="p-1.5 text-slate-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg"><Pencil size={14} /></button>
          )}
          <button title="History" onClick={() => setShowHist(v => !v)} className="p-1.5 text-slate-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg"><History size={14} /></button>
          {(item.status === 'archived' || item.status === 'rejected') ? (
            <button title="Restore (make active)" onClick={() => act('restore', () => api.post(`/api/ai/memory/${item.id}/restore`))}
              className="p-1.5 text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"><RotateCcw size={14} /></button>
          ) : (
            <button title="Archive (stops being used; can be restored)"
              onClick={() => { if (window.confirm(`Archive memory #${item.id}? It will no longer be used by the AI.`)) act('archive', () => api.delete(`/api/ai/memory/${item.id}`)); }}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      {item.status === 'pending' && item.replaces_id ? (
        <div className="mt-2 grid md:grid-cols-2 gap-2">
          <div className="bg-red-50/50 border border-red-100 rounded-lg p-2">
            <div className="text-[10px] font-semibold uppercase text-red-500 mb-1">Current #{item.replaces_id}</div>
            <pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-600">{orig ? `${orig.title}\n\n${orig.content}` : '…'}</pre>
          </div>
          <div className="bg-emerald-50/50 border border-emerald-100 rounded-lg p-2">
            <div className="text-[10px] font-semibold uppercase text-emerald-600 mb-1">Proposed update</div>
            <pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-600">{`${item.title}\n\n${item.content}`}</pre>
          </div>
        </div>
      ) : (
        <pre className="mt-2 whitespace-pre-wrap font-mono text-xs text-slate-600">
          {long && !expanded ? item.content.slice(0, 280) + '…' : item.content}
        </pre>
      )}
      {long && !(item.status === 'pending' && item.replaces_id) && (
        <button onClick={() => setExpanded(v => !v)} className="text-xs text-brand-700 hover:underline mt-1">{expanded ? 'Show less' : 'Show more'}</button>
      )}

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400">
        {item.tags && <span>Tags: {item.tags}</span>}
        <span>By {item.created_by || '—'} ({item.origin})</span>
        <span>Updated {fmtTime(item.updated_at)}</span>
        <span>Used {item.use_count}× · last {fmtTime(item.last_used)}</span>
        {item.review_note && <span>Note: {item.review_note}</span>}
      </div>
      {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
      {showHist && <HistoryPanel item={item} onClose={() => setShowHist(false)} onRestored={() => { setShowHist(false); onChanged(); }} />}
    </div>
  );
}

function SettingsBar({ settings, embedding, onSaved }) {
  const [s, setS] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => setS(settings), [settings]);
  if (!s) return null;
  const dirty = JSON.stringify(s) !== JSON.stringify(settings);
  const save = async () => {
    setBusy(true); setMsg('');
    try { const { data } = await api.put('/api/ai/memory/settings', s); onSaved(data); setMsg('Saved.'); }
    catch (e) { setMsg(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="text-xs text-slate-500">New memories saved by the AI
          <select value={s.approval_mode} onChange={e => setS({ ...s, approval_mode: e.target.value })}
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="approval">Require my approval</option>
            <option value="auto">Activate immediately</option>
          </select>
        </label>
        <label className="text-xs text-slate-500">Inject into chats
          <select value={s.inject_enabled} onChange={e => setS({ ...s, inject_enabled: e.target.value })}
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
            <option value="1">On</option>
            <option value="0">Off</option>
          </select>
        </label>
        <label className="text-xs text-slate-500">Max notes per prompt
          <input type="number" min="1" max="15" value={s.top_k} onChange={e => setS({ ...s, top_k: e.target.value })}
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
        </label>
        <label className="text-xs text-slate-500">Min relevance score (0–1)
          <input type="number" step="0.05" min="0" max="1" value={s.min_score} onChange={e => setS({ ...s, min_score: e.target.value })}
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
        </label>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="text-[11px] text-slate-400">
          Embedding {embedding ? 'available' : 'unavailable — keyword matching only'}. Notes you create or edit here apply immediately; approval only affects notes saved by the AI.
        </span>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-slate-500">{msg}</span>}
          <button onClick={save} disabled={!dirty || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
          </button>
        </div>
      </div>
    </div>
  );
}

function RetrievalTester() {
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [user, setUser] = useState('');
  const [mode, setMode] = useState('code');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true); setErr('');
    try { const { data } = await api.post('/api/ai/memory/search', { query: q, source: source || null, user: user || null, mode }); setRows(data.items || []); }
    catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><FlaskConical size={14} /> Test retrieval</div>
      <div className="text-[11px] text-slate-400">Shows which notes would be injected for a question, with scores — useful for tuning the minimum score.</div>
      <div className="flex flex-wrap gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} placeholder="e.g. P01 is slow again, many work processes on hold"
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-slate-200 rounded-lg" />
        <input value={source} onChange={e => setSource(e.target.value)} placeholder="Datasource (optional)" className="w-40 px-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
        <input value={user} onChange={e => setUser(e.target.value)} placeholder="User (optional)" className="w-32 px-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
        <select value={mode} onChange={e => setMode(e.target.value)} className="px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
          <option value="code">AI Coding / chat</option>
          <option value="sql">Text-to-SQL</option>
        </select>
        <button onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 text-white rounded-lg disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Test
        </button>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {rows && (rows.length === 0 ? <div className="text-xs text-slate-400">No note would be injected.</div> : (
        <div className="space-y-1">
          {rows.map(r => (
            <div key={r.id} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-slate-500 w-24 shrink-0">score {r.score}</span>
              <span className="text-slate-400">#{r.id}</span>
              {r.pinned && <Pin size={11} className="text-amber-600" />}
              <span className="text-slate-700 truncate">{r.title}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function AIMemory() {
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('');
  const [kind, setKind] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [form, setForm] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { status };
      if (q) params.q = q;
      if (scope) params.scope = scope;
      if (kind) params.kind = kind;
      const { data: d } = await api.get('/api/ai/memory', { params });
      setData(d);
    } catch (e) { setErr(errMsg(e)); }
    setLoading(false);
  }, [status, q, scope, kind]);

  useEffect(() => { load(); }, [load]);

  // Buka tab "Active" kalau tidak ada yang pending saat pertama dibuka.
  useEffect(() => {
    if (firstLoad && data && data.counts) {
      setFirstLoad(false);
      if (status === 'pending' && !data.counts.pending) setStatus('active');
    }
  }, [data, firstLoad, status]);

  const counts = (data && data.counts) || {};
  const items = (data && data.items) || [];

  return (
    <div className="space-y-4 pb-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2"><Brain size={18} className="text-brand-600" /> Memory</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Long-term memory shared by every AI chat in the dashboard. Relevant notes are added to the prompt automatically;
            the AI can save new ones with <code>memory_save</code>, or when you tell it to "remember this".
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={load} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" title="Refresh">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setForm({ ...EMPTY_FORM })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg">
            <Plus size={14} /> New memory
          </button>
        </div>
      </div>

      {data && <SettingsBar settings={data.settings} embedding={data.embedding} onSaved={(s) => setData(d => ({ ...d, settings: s }))} />}

      {form && (
        <MemoryForm initial={form} onCancel={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {STATUS_TABS.map(t => (
            <button key={t.key} onClick={() => setStatus(t.key)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md ${status === t.key ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {t.label}{t.key !== 'all' && counts[t.key] != null ? ` (${counts[t.key]})` : ''}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, content, tags…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg" />
        </div>
        <select value={scope} onChange={e => setScope(e.target.value)} className="px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
          <option value="">All scopes</option>
          <option value="global">Global</option>
          <option value="source">Datasource</option>
          <option value="user">User</option>
        </select>
        <select value={kind} onChange={e => setKind(e.target.value)} className="px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
          <option value="">All kinds</option>
          <option value="fact">Facts</option>
          <option value="skill">Skills</option>
        </select>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err}</div>}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-xl p-6 text-center">
          {status === 'pending' ? 'Nothing waiting for approval.' : 'No memories here yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(it => (
            <MemoryCard key={it.id} item={it} onChanged={load}
              onEdit={(m) => setForm({ id: m.id, title: m.title, content: m.content, kind: m.kind, scope: m.scope, scope_key: m.scope_key, tags: m.tags, pinned: !!m.pinned })} />
          ))}
        </div>
      )}

      <RetrievalTester />
    </div>
  );
}
